import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";

import { type ReactNode } from "react";

import { useDispatch } from "react-redux";

import { api } from "@state/apiSlice";

import ErrorBoundaryContent from "./error-boundary-content";

/**
 * UncaughtErrorBoundary
 *
 * This boundary component handles uncaught errors in the application that are not explicitly managed
 * (e.g., unexpected runtime errors). It leverages Sentry's `ErrorBoundary` to report these errors
 * and provide a fallback UI for the user.
 *
 * **Behavior**:
 * - Captures uncaught errors and logs them to Sentry.
 * - Displays a fallback UI (`ErrorBoundaryContent`) with an option to retry.
 * - On retry, resets the RTK Query API cache and clears the error state.
 *
 * **Features**:
 * - Integrated with Sentry for error tracking and reporting.
 * - Provides a seamless user experience with retry functionality.
 *
 * @param {ReactNode} children - The child components to render when no uncaught errors occur.
 *
 * @example
 * <UncaughtErrorBoundary>
 *   <YourComponent />
 * </UncaughtErrorBoundary>
 */
export default function UncaughtErrorBoundary({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();

  const onRetry = (resetError: () => void) => {
    dispatch(api.util.resetApiState());
    resetError();
  };

  return (
    <SentryErrorBoundary
      showDialog
      fallback={({ resetError, eventId }) => (
        <ErrorBoundaryContent
          type="SENTRY"
          onRetry={() => onRetry(resetError)}
          eventId={eventId}
        />
      )}
    >
      {children}
    </SentryErrorBoundary>
  );
}
